import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { MARKDOWN_TILLAGG } from '../lib/markdown'
import ExaminationsTabell from '../components/ExaminationsTabell'
import type { Lararhandledning, Prov } from '../types'
import { hittaArbetsomrade, laddaLararhandledning, laddaProv, lektionerForArbetsomrade, lektionsord } from '../lib/lektionsplanering'
import { kapitelIndex } from '../lib/content'
import { hamtaForladdad } from '../lib/forladdat'
import { useSidTitel } from '../lib/seo'
import ProvSektion from '../components/ProvSektion'
import FeedbackRuta from '../components/FeedbackRuta'

export default function ArbetsomradeSida() {
  const { aoId } = useParams<{ aoId: string }>()
  const ao = aoId ? hittaArbetsomrade(aoId) : null
  // Förladdade av förrenderingen (SSG); tomma i webbläsaren → effekten laddar.
  const forladdadHandledning = hamtaForladdad<Lararhandledning>(`lararhandledning:${aoId}`)
  const [handledning, setHandledning] = useState<Lararhandledning | null>(forladdadHandledning)
  const [prov, setProv] = useState<Prov | null>(() => hamtaForladdad(`prov:${aoId}`))
  const [laddad, setLaddad] = useState(forladdadHandledning !== null)
  useSidTitel(ao ? `Arbetsområde ${ao.ordning}: ${ao.titel} — lärarhandledning` : undefined)

  useEffect(() => {
    let aktiv = true
    setHandledning(null)
    setProv(null)
    setLaddad(false)
    if (!aoId) return
    Promise.all([laddaLararhandledning(aoId), laddaProv(aoId)]).then(([h, p]) => {
      if (!aktiv) return
      setHandledning(h)
      setProv(p)
      setLaddad(true)
    })
    return () => {
      aktiv = false
    }
  }, [aoId])

  if (!ao) {
    return (
      <div className="wrap">
        <div className="dokument">
          <h1>Arbetsområdet finns inte</h1>
          <Link className="knapp knapp-primar" to="/lararhandledning">
            Till arbetsområdena
          </Link>
        </div>
      </div>
    )
  }

  const lektioner = lektionerForArbetsomrade(ao.id)
  const kopplade = ao.kopplade_kapitel
    .map((id) => kapitelIndex.find((k) => k.id === id))
    .filter((k): k is NonNullable<typeof k> => Boolean(k))

  return (
    <div className="wrap">
      <header className="kapitelhuvud">
        <nav className="brodsmula" aria-label="Brödsmulor">
          <Link to="/lararhandledning">← Alla arbetsområden</Link>
        </nav>
        <span className="kapitel-nr">Arbetsområde {ao.ordning}</span>
        <h1>{ao.titel}</h1>

        <div className="ao-metarad">
          {kopplade.map((k) =>
            k.publicerad ? (
              <Link key={k.id} to={`/kapitel/${k.slug}`} className="kapitel-chip">
                Kap {k.id}: {k.titel}
              </Link>
            ) : (
              <span key={k.id} className="kapitel-chip kapitel-chip-inaktiv">
                Kap {k.id}: {k.titel}
              </span>
            ),
          )}
        </div>

        <div className="larandemal">
          <h2>Övergripande lärandemål</h2>
          <ul>
            {ao.overgripande_larandemal.map((mal) => (
              <li key={mal}>{mal}</li>
            ))}
          </ul>
        </div>

        <dl className="ao-fakta">
          <div>
            <dt>Lektioner</dt>
            <dd>{lektioner.length}</dd>
          </div>
          {ao.examinationsform && (
            <div className="ao-fakta-bred">
              <dt>Examinationsform</dt>
              <dd>{ao.examinationsform}</dd>
            </div>
          )}
        </dl>
      </header>

      <section aria-labelledby="handledning-rubrik">
        <div className="sektionshuvud-med-status">
          <h2 className="sektionsrubrik" id="handledning-rubrik">Lärarhandledning</h2>
        </div>

        {!laddad ? null : !handledning ? (
          <p className="sektionsingress">Ingen lärarhandledning är kopplad till det här arbetsområdet.</p>
        ) : (
          <div className="handledning">
            {/* Rubriksatta fält: tema, examination, förberedelser och ev. mer tid.
                Har källan inte migrerats än visas de utgående fälten i stället,
                så att sidan aldrig står tom under övergången. */}
            {handledning.tema ? (
              <>
                <div className="handledning-block">
                  <h3>Tema</h3>
                  <div className="dokument dokument-inbaddad">
                    <ReactMarkdown remarkPlugins={MARKDOWN_TILLAGG}>{handledning.tema}</ReactMarkdown>
                  </div>
                </div>

                {handledning.examination && (
                  <div className="handledning-block">
                    <h3>Examination</h3>
                    {handledning.examination.beskrivning && (
                      <div className="dokument dokument-inbaddad">
                        <ReactMarkdown remarkPlugins={MARKDOWN_TILLAGG}>{handledning.examination.beskrivning}</ReactMarkdown>
                      </div>
                    )}
                    <ExaminationsTabell moment={handledning.examination.moment} />
                  </div>
                )}

                {handledning.lararens_forberedelser && (
                  <div className="handledning-block">
                    <h3>Lärarens förberedelser</h3>
                    <div className="dokument dokument-inbaddad">
                      <ReactMarkdown remarkPlugins={MARKDOWN_TILLAGG}>{handledning.lararens_forberedelser}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {handledning.om_du_har_mer_tid && (
                  <div className="handledning-block handledning-mer-tid">
                    <h3>Om du har mer tid</h3>
                    <div className="dokument dokument-inbaddad">
                      <ReactMarkdown remarkPlugins={MARKDOWN_TILLAGG}>{handledning.om_du_har_mer_tid}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {handledning.tema_och_mal && (
                  <div className="handledning-block">
                    <h3>Tema och mål</h3>
                    <div className="dokument dokument-inbaddad">
                      <ReactMarkdown remarkPlugins={MARKDOWN_TILLAGG}>{handledning.tema_och_mal}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {handledning.kort_amnesbakgrund && (
                  <details className="handledning-block handledning-bakgrund">
                    <summary>Kort ämnesbakgrund</summary>
                    <div className="dokument dokument-inbaddad">
                      <ReactMarkdown remarkPlugins={MARKDOWN_TILLAGG}>{handledning.kort_amnesbakgrund}</ReactMarkdown>
                    </div>
                  </details>
                )}
              </>
            )}

            {(handledning.kallor_pedagogiska_pastaenden?.length ?? 0) > 0 && (
              <details className="handledning-block handledning-bakgrund">
                <summary>Källor för didaktiska påståenden</summary>
                <ol className="kallista">
                  {handledning.kallor_pedagogiska_pastaenden!.map((k) => (
                    <li key={k}>{k}</li>
                  ))}
                </ol>
              </details>
            )}
          </div>
        )}
      </section>

      <section aria-labelledby="lektioner-rubrik">
        <div className="sektionshuvud-med-status">
          <h2 className="sektionsrubrik" id="lektioner-rubrik">Lektioner</h2>
        </div>
        <p className="sektionsingress">
          Arbetsområdet omfattar {lektioner.length} {lektionsord(lektioner.length)}.
        </p>
        <div className="lektionslista">
          {lektioner.map((post) => (
            <Link
              key={post.lektion_id}
              to={`/lararhandledning/${ao.id}/lektion/${post.lektion_id}`}
              className="lektionskort"
            >
              <span className="lektionskort-nr">Lektion {post.lektionsnummer}</span>
              <h3>{post.titel}</h3>
              <div className="lektionskort-meta">
                <span>
                  {post.totalMinuter} min · Kap {post.kopplade_kapitel.join(', ')}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {laddad &&
        (prov ? (
          <ProvSektion prov={prov} />
        ) : (
          <section aria-labelledby="prov-rubrik">
            <h2 className="sektionsrubrik" id="prov-rubrik">Prov</h2>
            <p className="sektionsingress">Inget prov är kopplat till det här arbetsområdet.</p>
          </section>
        ))}

      <FeedbackRuta typ="arbetsomrade" objektId={ao.id} benamning="det här arbetsområdet" />
    </div>
  )
}
